import React, { Fragment, useEffect, useMemo, useRef, useState }  from 'react';
import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';
import SeccionPanel from '../../Components/SeccionPanel';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';
import useStorage from '../../Hooks/useStorage';

import noticiasHelper from '../../Helpers/Noticias';
import { recorrerPosiciones } from '../../Helpers/posiciones';

const NUEVA = { imagen: '', url: '', descripcion: '', orden: 1 };

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };

// Las imágenes viejas están en noticias/; las nuevas en imagenes/noticias/
const esImagenNoticia = url => typeof url === 'string' && (url.includes('/o/imagenes%2Fnoticias%2F') || url.includes('/o/noticias%2F'));

const enlaceValido = url => /^(\/\S*|https:\/\/\S+)?$/.test(url ?? '');

const Noticias = () => {
    const { editar, setEditar, index, setIndex, data, setData, recargar } = useAuth();

    const [noticia, setNoticia] = useState({ ...editar });
    const [nueva, setNueva] = useState(NUEVA);
    const [imagenEditar, setImagenEditar] = useState(null);
    const [imagenNueva, setImagenNueva] = useState(null);
    const [dimensiones, setDimensiones] = useState(null);
    const [seccionActiva, setSeccionActiva] = useState(null);
    // Remonta los formularios al limpiar: el input de archivo no es controlado
    const [version, setVersion] = useState(0);

    const [subiendo, setSubiendo] = useState(false);
    const [consulta, setConsulta] = useState('');
    const [respuestaStorage] = useStorage(consulta);
    const recargarTrasBorrar = useRef(false);

    useEffect(() => {
        obtenerNoticias();
    }, []);

    useEffect(() => {
        setNoticia({ ...editar });
    }, [editar]);

    useEffect(() => {
        if (respuestaStorage.action === 'agregar') guardarNueva(respuestaStorage.url);
        if (respuestaStorage.action === 'agregarActualizado') guardarEdicion(respuestaStorage.url);
        if (respuestaStorage.action === 'eliminarSinConfirmar') {
            setConsulta('');
            // recargar() remonta la vista; si se llama antes de esta respuesta, el borrado no termina
            if (recargarTrasBorrar.current) recargar();
            else setSubiendo(false);
        }
    }, [respuestaStorage]);

    const obtenerNoticias = async () => {
        let res = await noticiasHelper.obtenerNoticias();
        if (res.success) {
            setData(res.data);
        } else {
            Swal.fire('Error!', 'No se pudieron obtener las noticias, codigo: ' + res.code, 'error');
        }
    }

    const hayCambiosEditar = Boolean(editar._id) && JSON.stringify(noticia) !== JSON.stringify(editar);
    const hayCambiosNueva = Boolean(nueva.url || nueva.descripcion || imagenNueva);

    // actualizarBorrador modifica `data` en vivo para la vista previa; `editar` conserva el original
    const limpiarBorradores = () => {
        if (index !== false) setData(data => data.map((dato, i) => (i === index ? editar : dato)));
        setEditar({});
        setIndex(false);
        setNueva(NUEVA);
        setImagenEditar(null);
        setImagenNueva(null);
        setDimensiones(null);
        setSeccionActiva(null);
        setVersion(version => version + 1);
    }

    const panel = usePanelEdicion({ hayCambios: hayCambiosEditar || hayCambiosNueva, onCerrar: limpiarBorradores });

    const abrirPanel = async (nombre, alAbrir) => {
        if ((hayCambiosEditar || hayCambiosNueva) && !(await confirmarDescartar())) return;
        limpiarBorradores();
        alAbrir?.();
        panel.abrir(nombre);
    }

    const abrirAgregar = () => abrirPanel('agregar', () => {
        setNueva({ ...NUEVA, orden: data.length + 1 });
    });

    const seleccionar = ({ id }) => {
        const indice = data.findIndex(dato => dato._id === id);
        if (indice < 0) return;
        abrirPanel('editar', () => {
            setEditar(indice === index ? editar : data[indice]);
            setIndex(indice);
        });
    }

    const datosPreview = useMemo(() => {
        if (nueva.imagen) return recorrerPosiciones([...data, { ...nueva, _id: 'nuevo' }], 'orden', 'nuevo', nueva.orden);
        if (editar._id) return recorrerPosiciones(data, 'orden', editar._id, noticia.orden);
        return data;
    }, [data, nueva, editar._id, noticia.orden]);

    // seccion=enterate: en preview el sitio pinta solo la sección de noticias
    const { iframeRef, src } = usePreviewSitio({
        page: 'noticias',
        ruta: '/?seccion=enterate',
        data: datosPreview,
        onSelect: seleccionar
    });

    const actualizarBorrador = cambios => {
        setNoticia(noticia => ({ ...noticia, ...cambios }));
        setData(data => data.map((dato, i) => (i === index ? { ...dato, ...cambios } : dato)));
    }

    const cambiarNueva = cambios => setNueva(nueva => ({ ...nueva, ...cambios }));

    // El iframe es de otro origen y no puede leer un blob: del admin, por eso se manda como data URL
    const leerImagen = (e, cambiar, setImagen) => {
        const archivo = e.target.files[0];
        if (!archivo) return;
        const lector = new FileReader();
        lector.addEventListener('load', () => {
            setImagen(archivo);
            cambiar({ imagen: lector.result });
            const imagen = new Image();
            imagen.onload = () => setDimensiones({ ancho: imagen.naturalWidth, alto: imagen.naturalHeight });
            imagen.src = lector.result;
        });
        lector.readAsDataURL(archivo);
    }

    // Al agregar hay una posición más: la nueva puede ir al final
    const maximaPosicion = esNueva => data.length + (esNueva ? 1 : 0);

    const errores = (borrador, imagen, maximo) => {
        if (!imagen) return 'Faltan datos por llenar: Imagen';
        if (!enlaceValido(borrador.url)) return 'El enlace debe empezar con / o https://';
        if (!Number.isInteger(borrador.orden) || borrador.orden < 1 || borrador.orden > maximo) return 'La posición debe ser un número entre 1 y ' + maximo;
        return null;
    }

    const borrarImagen = (url, recargarDespues) => {
        recargarTrasBorrar.current = recargarDespues;
        setConsulta({ consulta: 'eliminarSinConfirmar', urlFile: url });
    }

    const subirImagen = (accion, archivo) => {
        setSubiendo(true);
        setConsulta({ consulta: accion, name: 'noticia_' + Date.now(), folder: 'imagenes/noticias/', file: archivo, url: '/noticias' });
    }

    const agregar = e => {
        e.preventDefault();
        const error = errores(nueva, imagenNueva, maximaPosicion(true));
        if (error) return Swal.fire('Error!', error, 'error');
        subirImagen('agregar', imagenNueva);
    }

    const guardarNueva = async url => {
        // useStorage solo vuelve a ejecutarse si cambia el texto de la consulta
        setConsulta('');
        if (!url) {
            setSubiendo(false);
            return Swal.fire('Error!', 'No se pudo subir la imagen', 'error');
        }
        const res = await noticiasHelper.agregarNoticia({ ...nueva, imagen: url });
        if (res?.success) return recargar();
        // Sin registro que la use, la imagen recién subida quedaría huérfana
        borrarImagen(url, false);
    }

    const actualizar = e => {
        e.preventDefault();
        const error = errores(noticia, noticia.imagen, maximaPosicion(false));
        if (error) return Swal.fire('Error!', error, 'error');
        if (imagenEditar) return subirImagen('agregarActualizado', imagenEditar);
        setSubiendo(true);
        guardarEdicion();
    }

    // `url` solo llega cuando se subió una imagen nueva
    const guardarEdicion = async url => {
        setConsulta('');
        if (imagenEditar && !url) {
            setSubiendo(false);
            return Swal.fire('Error!', 'No se pudo subir la imagen', 'error');
        }
        const anterior = editar.imagen;
        const res = await noticiasHelper.editarNoticia({ ...noticia, imagen: url ?? anterior }, editar._id);
        if (!res?.success) {
            if (url) return borrarImagen(url, false);
            return setSubiendo(false);
        }
        // Se borra hasta que el registro ya apunta a la nueva, para que el sitio nunca quede con una imagen rota
        if (url && esImagenNoticia(anterior)) return borrarImagen(anterior, true);
        recargar();
    }

    const eliminar = async () => {
        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            titleText: '¿Eliminar esta noticia?',
            text: 'También se elimina su imagen.',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!isConfirmed) return;
        setSubiendo(true);
        const res = await noticiasHelper.eliminarNoticia(editar._id);
        if (!res?.success) return setSubiendo(false);
        if (esImagenNoticia(editar.imagen)) return borrarImagen(editar.imagen, true);
        recargar();
    }

    const seccion = clave => ({ id: clave, activa: seccionActiva === clave, onFocus: () => setSeccionActiva(clave) });

    const campos = (borrador, cambiar, setImagen, imagen, prefijo) => {
        const id = campo => prefijo + campo;
        const leer = e => cambiar({ [e.target.name]: e.target.value });
        const noCuadrada = imagen && dimensiones && dimensiones.ancho !== dimensiones.alto;

        return (
            <Fragment>
                <SeccionPanel numero={1} titulo='Imagen' ubicacion='Tarjeta del carrusel "Entérate", debajo de las licenciaturas.' {...seccion('imagen')}>
                    {borrador.imagen ? (
                        <img alt={borrador.descripcion || 'Imagen de la noticia'} src={borrador.imagen} style={{width:"100%", maxWidth:"220px", borderRadius:"10px"}} />
                    ) : (
                        <p style={{color:"#757575"}}>Aún no hay imagen.</p>
                    )}
                    <div className="file-field input-field">
                        <div className="btn" style={{backgroundColor:"#00496C"}}>
                            <span>Archivo</span>
                            <input onChange={e => leerImagen(e, cambiar, setImagen)} type="file" accept="image/*" />
                        </div>
                        <div className="file-path-wrapper">
                            <input className="file-path" type="text" placeholder="Selecciona una imagen" value={imagen?.name ?? ''} readOnly />
                        </div>
                    </div>
                    <p style={{color:"#757575", fontSize:"13px", margin:"0 0 10px"}}>Se recomienda imagen cuadrada de 1080 x 1080 px.</p>
                    {noCuadrada && (
                        <p style={{color:"#8a6d00", background:"#fff8e1", fontSize:"13px", padding:"6px 8px", borderRadius:"4px", margin:"0 0 10px"}}>
                            La imagen mide {dimensiones.ancho} x {dimensiones.alto}; se verá con distinta altura que las demás.
                        </p>
                    )}
                    {prefijo === 'editar_' && (
                        <p style={{color:"#757575", fontSize:"13px", margin:"0 0 20px"}}>Al reemplazarla, la imagen anterior se elimina.</p>
                    )}
                    <div className="input-field">
                        <input onChange={leer} value={borrador.descripcion ?? ''} id={id('descripcion')} name='descripcion' type="text" className="validate" maxLength={200} />
                        <label className='active' htmlFor={id('descripcion')}>Texto alternativo</label>
                    </div>
                    <p style={estiloAyuda}>Describe la imagen para buscadores y lectores de pantalla; no se ve en la página.</p>
                </SeccionPanel>

                <SeccionPanel numero={2} titulo='Enlace' ubicacion='Al hacer clic en la imagen.' {...seccion('enlace')}>
                    <div className="input-field">
                        <input onChange={leer} value={borrador.url ?? ''} id={id('url')} name='url' type="text" className="validate" />
                        <label className='active' htmlFor={id('url')}>Enlace</label>
                    </div>
                    <p style={enlaceValido(borrador.url) ? estiloAyuda : { ...estiloAyuda, color: "#c62828" }}>
                        {enlaceValido(borrador.url)
                            ? 'Opcional. Ruta del sitio (/cursos-diplomados) o enlace completo (https://…); los enlaces externos se abren en otra pestaña. Vacío = la imagen no es clicable.'
                            : 'El enlace debe empezar con / o https://'}
                    </p>
                </SeccionPanel>

                <SeccionPanel numero={3} titulo='Posición' ubicacion='Orden dentro del carrusel.' {...seccion('posicion')}>
                    <div className="input-field">
                        <input
                            onChange={e => cambiar({ orden: e.target.value === '' ? '' : Number(e.target.value) })}
                            value={borrador.orden ?? 1}
                            id={id('orden')}
                            type="number"
                            min="1"
                            max={maximaPosicion(prefijo === 'nueva_')}
                            className="validate"
                        />
                        <label className='active' htmlFor={id('orden')}>Posición</label>
                    </div>
                    <p style={estiloAyuda}>1 = primera del carrusel (de 1 a {maximaPosicion(prefijo === 'nueva_')}). Al cambiarla, las demás noticias se recorren.</p>
                </SeccionPanel>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <SideNav />
            {subiendo && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'agregar'}
                    titulo='Agregar noticia'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosNueva}
                    pie={
                        <button type='submit' form='form_agregar_noticia' className='boton_admin' disabled={!hayCambiosNueva}>
                            Agregar noticia
                        </button>
                    }
                >
                    {panel.abierto === 'agregar' && (
                        <form id='form_agregar_noticia' key={version} onSubmit={agregar}>
                            {campos(nueva, cambiarNueva, setImagenNueva, imagenNueva, 'nueva_')}
                        </form>
                    )}
                </PanelEdicion>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo='Editar noticia'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosEditar}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_peligro' onClick={eliminar}>
                                Eliminar
                            </button>
                            <button type='submit' form='form_editar_noticia' className='boton_admin' disabled={!hayCambiosEditar}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    {panel.abierto === 'editar' && editar._id && (
                        <form id='form_editar_noticia' key={version} onSubmit={actualizar}>
                            {campos(noticia, actualizarBorrador, setImagenEditar, imagenEditar, 'editar_')}
                        </form>
                    )}
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo='Vista previa de la sección Entérate'
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado='Noticias'
                    ayuda='Haz clic en una noticia de la sección Entérate para editarla'
                    acciones={
                        <button type='button' className='boton_admin' onClick={abrirAgregar}>
                            + Agregar noticia
                        </button>
                    }
                />
            </div>
        </Fragment>
    );
}

export default Noticias;
